import moment from 'moment'
import clsx from 'clsx'
import {orderBy} from 'lodash'
import React from 'react'
import {Redirect, Link} from 'react-router-dom'

import {makeStyles} from 'external/material'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import CloseIcon from '@mui/icons-material/Close'
import TextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack';
import {faStickyNote} from "@fortawesome/free-solid-svg-icons"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"

import Navigation from './Navigation'
import Sections from './Sections'
import Section from './Section'
import Question from './Question'
import {toDisplayPoints} from '../../../helpers/utils'
import {createPost} from '../../../helpers/command'
import PreviewPdf from '../preview-pdf'
import Comments from './Comments'
import Standards from './Standards'

const useStyles = makeStyles(assessmentStyles)
 
export default function ReviewAssessment ({context}) {

  const classes = useStyles()
  const [cleared, clear] = React.useState(false)

  const [commentsOpened, openComments] = React.useState(false)

  const {quizAssessmentToReview: assessment, assessment: assessmentId,  translate, sectionId, link, quizTypesMap} = context

  const {studentName, content = {}, results = {}, finished, properties = {}} = assessment 

  const {title, type} = content

  const sections = assessment.id == assessmentId
    ? orderBy(Object.values(content.sections || {}),'index')
    : []

  const redirect = sections.length > 0 && !sectionId
  const redirectLink = redirect && link({sectionId: sections[0].id})

  const typeName = translate(quizTypesMap[type])

  const isSection = !context.questionId

  const closeLink = link({}, context.quizEditFrom || '/:languageId/:role/:page/assigned', {query: context.quizEditFromQuery})

  const {points, total} = Object.values(results).reduce((acc, {points, total}) => ({total: acc.total + total, points: acc.points + points}), {total: 0, points: 0})

  const pointsText = `${toDisplayPoints(points + (properties.bonus || 0))}/${toDisplayPoints(total)}`

  const changeBonus = (bonus) => context.updateState({quizAssessmentToReview: {properties: {bonus: {$set: bonus}}}})

  function handleBonusChange(e) {
    const cleared = e.target.value == ''
    const value = cleared ? 0 : parseInt(e.target.value)
    const points = Math.max(0, Math.min(value, 9999))
    changeBonus(points)
    clear(cleared) 
  }

  function handlePointsChange(e) {
    const points = Math.max(0, Math.min(value, result.total))
    changePoints(points, cleared)
  }

  function sendProperties() {
    createPost('quiz','saveReviewProperties')({id: context.assessment, properties: context.quizAssessmentToReview.properties})
  }

  function saveToPdf() {
    context.updateState({quizAssessmentForPdfId: {$set: context.assessment}})
  }

  const displayBonus = cleared ? '' : toDisplayPoints(properties.bonus)

  return redirectLink
    ? (<Redirect to={redirectLink} />)
    : (
        <Box className={classes.root}>
          <Box className={classes.preview}>
            <Paper className={classes.view}>
              <Box className={classes.viewHeader}>
                <Box className={classes.studentNameAndActions}>
                  <Box className={classes.studentName}>
                    {studentName}
                  </Box>
                  <Box className={classes.actions}>
                    <Box className={clsx(classes.button, classes.saveAsPdf)} onClick={saveToPdf}>
                      <Box className="label">{translate('save-as-pdf')}</Box>
                    </Box>
                    <Box className={clsx(classes.button, classes.comment)} onClick={() => openComments(true)}>
                      <Box className="icon annotation"><FontAwesomeIcon icon={faStickyNote} /></Box>
                      <Box className="label">{translate('annotations')}</Box>
                    </Box>
                    <Comments isOpen={commentsOpened} closeDialog={() => openComments(false)} context={context} />
                    <PreviewPdf context={context} />
                  </Box>
                </Box>
                <Box className={classes.title}>
                  <Box className="type">{typeName}:</Box>
                  <Box className="title">{title}</Box>
                </Box>
                <Box className={classes.results}>
                  <Box className={classes.finished}>
                    <Box className="label">{translate('assessment-sent-on')}:</Box>
                    <Box className="date">{moment(finished).format(translate('assessment-sent-on-format'))}</Box>
                  </Box>
                  <Box className={classes.bonus}>
                    <Box className="label">{translate('bonus')}:</Box>
                      <TextField
                        className="bonusInput"
                        InputProps={{inputProps: {min: 0, max: 9999}}}
                        value={displayBonus}
                        type="number"
                        onChange={handleBonusChange}
                        onBlur={sendProperties}
                      />
                  </Box>
                  <Box className={classes.points}>
                    <Box className="label">{translate('points')}:</Box>
                    <Box className="points">{pointsText}</Box>
                  </Box>
                </Box>
              </Box>
              <Box className={classes.viewContent}>
                {isSection && (<Section {...{sections, context}} />)}
                {!isSection && (<Question {...{sections, context}} />)} 
              </Box>
              <Box className={classes.viewNavigation}>
                <Stack
                  direction='row'
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                >
                    <Standards context={context} />
                    <Navigation {...{sections, context}} />
                </Stack>
              </Box>
            </Paper>
            <Box className={classes.side}>
              <Box className={classes.closeWrapper}>
                <Link className={clsx(classes.button, classes.close)} to={closeLink}>
                  <Box className="icon"><CloseIcon /></Box>
                  <Box className="label">{translate('close')}</Box>
                </Link>
              </Box>
              <Sections {...{sections, context}} />
            </Box>
          </Box>
        </Box>
      )
}

export function assessmentStyles (theme) {

  const verticalCentering = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  }

  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      overflowY: 'hidden'
    },
    closeWrapper: {
      display: 'flex',
      marginBottom: 15,
      justifyContent: 'flex-end'
    },
    button: {
      textDecoration: 'none',
      color: '#fff',
      display: 'flex',
      padding: '7px 15px 7px 12px',
      backgroundColor:'#2ca5ec',
      borderRadius: 3,
      '& >div': {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      },
      '& .icon': {
        marginTop: -3,
        marginRight: 3,
        fontSize: '8pt',
      },
      '& .label': {
        fontSize: '13pt'
      },
      cursor: 'pointer',
      '&:hover': {
      },
    },
    bonus: {
      display: 'flex',
      marginRight: 20,
      '& .value': {
        marginLeft: 5,
        minWidth: 40,
        padding: '8px 5px 9px 5px',
        borderRadius: 2,
        backgroundColor: "#cfe1eb"
      },
      '& .bonusInput': {
        marginLeft: 5,
        paddingLeft: 5,
        minWidth: 40,
        backgroundColor: '#fff',
        borderRadius: 3,
        '& .MuiInput-underline:after': {
          borderBottom: 0,
          transition: 'none'
        },
        '& .MuiInput-underline:before': {
          borderBottom: 0,
          transition: 'none'
        }
      }
   },
    comment: {
      '& .icon': {
        marginTop: -2,
        marginRight: 8,
        fontSize: '12pt',
        color: '#fff'
      }
    },
    saveAsPdf: {
      marginRight: 20,
    },
    preview: {
      display: 'flex',
      padding: 10,
      flexGrow: 1,
      overflowY: 'hidden'
    },
    view: {
      flexGrow: 1,
      overflowY: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    },
    viewHeader: {
      display: 'flex',
      flexDirection: 'column',
      color: '#4d5361',
      padding: 20,
      borderBottom: 'solid 1px #d3d8db',
    },
    studentNameAndActions: {
      display: 'flex'
    },
    title: {
      display:  'flex',
      marginTop: 15,
      '& > .type': {
        fontWeight: 'bold',
        marginRight: 3
      }
    },
    studentName: {
      fontWeight: 'bold',
      fontSize: '14pt',
      flexGrow: 1
    },
    actions: {
      display: 'flex'
    },
    results: {
      marginTop: 10,
      display: 'flex',
      '& .label': {
        fontWeight: 'bold',
        marginRight: 3
      },
      '& > div > div': {
        ...verticalCentering
      }
    },
    finished: {
      display: 'flex',
      flexGrow: 1
    },
    points: {
      display: 'flex'
    },
    total: {
      marginLeft: 20,
      display: 'flex'
    },
    viewContent: {
      display: 'flex',
      flexGrow: 1,
      overflowY: 'hidden',
    },
    viewNavigation: {
      borderTop: 'solid 1px #d3d8db',
      padding: 20
    },
    side: {
      marginLeft: 25,
      display: 'flex',
      flexDirection: 'column',
      minWidth: 255,
      maxWidth: 255
    }
  }
}
